import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Quote } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import Reveal from "../components/Reveal";
import { guestSpots, studio } from "../mock";
import { errorMessage } from "../lib/api";
import { EMAIL_RE, submitForm } from "../lib/forms";
import { cn } from "../lib/utils";

const initial = { firstName: "", lastName: "", email: "", message: "", agree: false };

const Field = ({ id, label, error, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
      {label}
    </Label>
    {children}
    {error && (
      <p className="text-xs text-primary" data-testid={`error-${id}`}>
        {error}
      </p>
    )}
  </div>
);

const inputCls =
  "h-12 rounded-none border-border bg-transparent px-4 text-base placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";

const GuestSpots = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "First name is required.";
    if (!form.lastName.trim()) e.lastName = "Last name is required.";
    if (!EMAIL_RE.test(form.email)) e.email = "Enter a valid email address.";
    if (form.message.trim().length < 10) e.message = "Tell us a little more (10+ characters).";
    if (!form.agree) e.agree = "Please agree to Avalon's ethos to continue.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitForm("guest_spot", {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        agreed: true,
      });
      setSubmitted({ email: form.email.trim() });
      toast.success(guestSpots.successTitle, { description: "Your guest spot request has been sent." });
    } catch (err) {
      toast.error(errorMessage(err, "Could not send your request. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-[76px]" data-testid="guest-spots-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={studio.images.guestSpots} alt="Tattoo artist at work" className="ken-burns h-full w-full object-cover grayscale" />
          <div className="absolute inset-0 bg-background/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-background/10" />
        </div>
        <div className="container-x relative pb-20 pt-24 lg:pb-28 lg:pt-36">
          <p className="eyebrow hero-in hero-in-1 flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-primary" />
            Travelling artists welcome
          </p>
          <h1 className="display hero-in hero-in-2 mt-6 text-[clamp(3.5rem,11vw,11rem)] uppercase leading-[0.82] tracking-wide">
            Guest Spot
            <br />
            <span className="text-stroke">Registry</span>
          </h1>
          <p className="hero-in hero-in-3 mt-8 max-w-xl text-base leading-relaxed text-foreground/80 md:text-lg">
            {guestSpots.subtitle} {guestSpots.intro}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="container-x grid gap-16 py-24 lg:grid-cols-12 lg:gap-20 lg:py-32">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow">Our ethos</p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="display mt-6 text-[clamp(2.5rem,5vw,4.5rem)] uppercase leading-[0.9] tracking-wide">
              Work out of Avalon while you&apos;re on the road.
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">{studio.welcomeBody}</p>
          </Reveal>
          <Reveal delay={240} className="mt-10 border-l-2 border-primary pl-6">
            <Quote className="h-5 w-5 text-primary" />
            <p className="mt-3 font-serif text-xl italic leading-relaxed text-foreground/90 md:text-2xl">{guestSpots.agreement}</p>
          </Reveal>
          <Reveal delay={300} className="mt-10 grid grid-cols-2 gap-6 border-t border-border pt-8 text-sm">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Studio</p>
              <p className="mt-2 font-medium">
                {studio.address.line1}
                <br />
                {studio.address.city}, {studio.address.province}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Hours</p>
              <p className="mt-2 font-medium">
                {studio.hours.label}
                <br />
                {studio.hours.open} – {studio.hours.close}
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={120} className="lg:col-span-7">
          <div className="border border-border bg-card p-7 md:p-10">
            {submitted ? (
              <div className="flex flex-col items-start gap-6 py-6" data-testid="guest-form-success">
                <span className="flex h-14 w-14 items-center justify-center bg-primary text-primary-foreground">
                  <CheckCircle2 className="h-7 w-7" />
                </span>
                <h3 className="display text-5xl uppercase leading-none tracking-wide">{guestSpots.successTitle}</h3>
                <p className="max-w-md text-base leading-relaxed text-muted-foreground">
                  {guestSpots.successBody} We&apos;ll reply to <span className="font-semibold text-foreground">{submitted.email}</span>.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button asChild className="h-12 rounded-none px-7 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="guest-return-home">
                    <Link to="/">
                      <ArrowLeft className="mr-2 h-4 w-4" /> Return Home
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 rounded-none border-foreground/30 bg-transparent px-7 text-[11px] font-semibold uppercase tracking-[0.2em] hover:bg-foreground hover:text-background"
                    onClick={() => {
                      setSubmitted(null);
                      setForm(initial);
                    }}
                    data-testid="guest-send-another"
                  >
                    Send another
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="guest-form">
                <div className="mb-8 flex items-end justify-between gap-4">
                  <h3 className="display text-4xl uppercase leading-none tracking-wide md:text-5xl">{guestSpots.formTitle}</h3>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">* required</span>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <Field id="firstName" label="First name *" error={errors.firstName}>
                    <Input
                      id="firstName"
                      value={form.firstName}
                      onChange={(e) => update("firstName", e.target.value)}
                      placeholder="Jane"
                      className={cn(inputCls, errors.firstName && "border-primary")}
                      data-testid="guest-first-name"
                    />
                  </Field>
                  <Field id="lastName" label="Last name *" error={errors.lastName}>
                    <Input
                      id="lastName"
                      value={form.lastName}
                      onChange={(e) => update("lastName", e.target.value)}
                      placeholder="Doe"
                      className={cn(inputCls, errors.lastName && "border-primary")}
                      data-testid="guest-last-name"
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field id="email" label="Email *" error={errors.email}>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="you@studio.com"
                        className={cn(inputCls, errors.email && "border-primary")}
                        data-testid="guest-email"
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field id="message" label="Message *" error={errors.message}>
                      <Textarea
                        id="message"
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        placeholder="Tell us about your style, portfolio links, and the dates you're looking at."
                        rows={6}
                        className={cn(
                          "min-h-[160px] rounded-none border-border bg-transparent px-4 py-3 text-base placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0",
                          errors.message && "border-primary"
                        )}
                        data-testid="guest-message"
                      />
                    </Field>
                  </div>
                </div>

                <div className="mt-8 flex items-start gap-3 border border-border p-4">
                  <Checkbox
                    id="agree"
                    checked={form.agree}
                    onCheckedChange={(v) => update("agree", Boolean(v))}
                    className="mt-0.5 rounded-none border-foreground/40 data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                    data-testid="guest-agree"
                  />
                  <div>
                    <Label htmlFor="agree" className="cursor-pointer text-sm leading-relaxed text-foreground/85">
                      {guestSpots.agreement}
                    </Label>
                    {errors.agree && (
                      <p className="mt-2 text-xs text-primary" data-testid="error-agree">
                        {errors.agree}
                      </p>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="mt-8 h-12 w-full rounded-none text-[11px] font-semibold uppercase tracking-[0.2em] sm:w-auto sm:px-10"
                  data-testid="guest-submit"
                >
                  {submitting ? "Sending…" : "Send"}
                  {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default GuestSpots;
